import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Collapse,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Navbar from "../components/Navbar.js";
import { backendRoot } from "../config";

function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function UserRow({ user }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 48 }}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{user.name}</TableCell>
        <TableCell>{user.email}</TableCell>
        <TableCell align="center" sx={{ fontWeight: 700, color: "#22577A" }}>{user.login_count}</TableCell>
        <TableCell>{formatDateTime(user.last_login)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ pb: 0, pt: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1.5, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: "#172033" }}>
                Login History
              </Typography>
              {user.login_logs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No logins recorded.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Timestamp</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>IP Address</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {user.login_logs.map((log, idx) => (
                      <TableRow key={log.id}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{formatDateTime(log.logged_in_at)}</TableCell>
                        <TableCell>{log.ip_address || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function ClientLogs() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${backendRoot}/api/client-login-logs/`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(`Failed to load logs: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <Box sx={{ minHeight: "100vh", background: "#F5F7FB" }}>
      <Navbar />
      <Box sx={{ maxWidth: 1100, mx: "auto", px: 3, py: 4 }}>
        <Typography variant="h5" fontWeight={800} sx={{ mb: 1, color: "#172033" }}>
          Client Login Logs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Login activity for external client users. Click a row to see full timestamp history.
        </Typography>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
        )}
        {!loading && !error && (
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(15,23,42,0.07)" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#F5F7FB" }}>
                  <TableCell />
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Login Count</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Last Login</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#667085" }}>
                      No client users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => <UserRow key={user.id} user={user} />)
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
